import React from "react";
import { Users, Repeat, ScanSearch, Cpu, ShieldCheck, GitBranch, LayoutDashboard, ArrowDown } from "lucide-react";

const STEPS = [
  { icon: Users, label: "Your people & systems", sub: "Operators, analysts, connected workflows", tag: "SOURCE" },
  { icon: Repeat, label: "Bifröst Relay", sub: "Connect · normalise · route", tag: "NORSE3" },
  { icon: ScanSearch, label: "Aegis input control", sub: "Inspect prompts, uploads and requests", tag: "NORSE3" },
  { icon: Cpu, label: "AI / model / agent", sub: "The system you choose to deploy — unchanged", tag: "EXTERNAL" },
  { icon: ShieldCheck, label: "Aegis output & action control", sub: "Inspect responses and intended actions", tag: "NORSE3" },
  { icon: GitBranch, label: "Allow · Warn · Redact · Block · Hold · Escalate", sub: "Your configured action applies", tag: "NORSE3" },
  { icon: LayoutDashboard, label: "Heimdall oversight", sub: "Human review, triage and reporting", tag: "NORSE3" },
];

const tagStyle = (tag) =>
  tag === "EXTERNAL"
    ? "border-signal-amber/40 text-signal-amber bg-signal-amber/[0.06]"
    : tag === "SOURCE"
    ? "border-white/15 text-muted-foreground bg-white/[0.03]"
    : "border-primary/30 text-primary bg-primary/[0.06]";

export default function ArchitectureFlow() {
  return (
    <div className="rounded-lg border border-white/10 bg-navy-900/70 p-5 sm:p-6 shadow-2xl shadow-black/40" data-testid="defence-architecture">
      <div className="flex items-center justify-between">
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">The control & evidence path</p>
        <span className="text-[0.7rem] text-muted-foreground/70">Illustrative</span>
      </div>

      <div className="mt-4 space-y-1.5">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const external = s.tag === "EXTERNAL";
          return (
            <div key={s.label}>
              <div
                className={`flex items-center gap-3 rounded-xl border px-3.5 py-3 ${
                  external ? "border-signal-amber/30 bg-signal-amber/[0.04]" : "border-white/10 bg-navy-800/60"
                }`}
              >
                <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${external ? "bg-signal-amber/12 text-signal-amber" : "bg-primary/12 text-primary"}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-foreground leading-tight">{s.label}</span>
                  <span className="block text-xs text-muted-foreground leading-tight mt-0.5">{s.sub}</span>
                </span>
                <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[0.6rem] font-semibold tracking-wide ${tagStyle(s.tag)}`}>{s.tag}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className="flex justify-center py-0.5" aria-hidden="true">
                  <ArrowDown className="h-3.5 w-3.5 text-muted-foreground/40" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {["Policy", "Human oversight", "Incident review", "Evidence"].map((x) => (
          <span key={x} className="rounded-lg border border-white/10 bg-navy-800/60 px-2.5 py-2 text-center text-[0.72rem] font-medium uppercase tracking-wide text-foreground/85">
            {x}
          </span>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        norse3 sits <span className="text-foreground font-medium">around</span> the AI you deploy — it does not replace the underlying model, agent or system.
      </p>
    </div>
  );
}
